'use client'

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import type { RefObject, ReactNode as ReactNodeType } from 'react'

export interface WorkspaceFloatingActionConfig {
  label: string
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  loadingContent?: ReactNodeType
  helperText?: string
  eyebrow?: ReactNodeType
  title?: ReactNodeType
  description?: ReactNodeType
  tone?: 'light' | 'dark'
  widthClassName?: string
  anchorRef?: RefObject<HTMLElement | null>
}

const WorkspaceFloatingActionConfigContext = createContext<WorkspaceFloatingActionConfig | null>(null)
const WorkspaceFloatingActionSetterContext = createContext<((config: WorkspaceFloatingActionConfig | null) => void) | null>(null)

export function WorkspaceFloatingActionProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<WorkspaceFloatingActionConfig | null>(null)
  const setFloatingAction = useCallback((nextConfig: WorkspaceFloatingActionConfig | null) => {
    setConfig((previousConfig) => (previousConfig === nextConfig ? previousConfig : nextConfig))
  }, [])

  return (
    <WorkspaceFloatingActionSetterContext.Provider value={setFloatingAction}>
      <WorkspaceFloatingActionConfigContext.Provider value={config}>
        {children}
      </WorkspaceFloatingActionConfigContext.Provider>
    </WorkspaceFloatingActionSetterContext.Provider>
  )
}

export function useWorkspaceFloatingActionConfig() {
  return useContext(WorkspaceFloatingActionConfigContext)
}

export function useSetWorkspaceFloatingAction() {
  const context = useContext(WorkspaceFloatingActionSetterContext)
  if (!context) {
    throw new Error('useSetWorkspaceFloatingAction must be used within WorkspaceFloatingActionProvider')
  }
  return context
}
